import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import { useSite } from "../context/SiteContext";
import { submitContact } from "../lib/api";

export default function Contact() {
  const { brand } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent. We'll respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32 pb-24 max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <SectionLabel>Contact</SectionLabel>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-light max-w-4xl">Let&apos;s <em className="not-italic font-medium">talk</em>.</h1>
        <p className="mt-6 text-muted-foreground max-w-2xl">
          Reach {brand.name} on WhatsApp, email or the form below — a real person on our Shillong team will write back, often within the hour.
        </p>
      </Reveal>

      <div className="mt-16 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <a href={`tel:+91${brand.phone}`} className="flex items-center gap-4 rounded-2xl border border-border p-5 hover:border-primary transition" data-testid="contact-call">
            <span className="h-11 w-11 grid place-items-center rounded-full bg-primary/10 text-primary"><Phone className="h-4 w-4" /></span>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Phone</div>
              <div className="font-medium">+91 {brand.phone}</div>
            </div>
          </a>
          <a href={`https://wa.me/91${brand.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-2xl border border-border p-5 hover:border-primary transition" data-testid="contact-whatsapp">
            <span className="h-11 w-11 grid place-items-center rounded-full bg-emerald-500/10 text-emerald-600"><MessageCircle className="h-4 w-4" /></span>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">WhatsApp</div>
              <div className="font-medium">+91 {brand.whatsapp}</div>
            </div>
          </a>
          <a href={`mailto:${brand.email}`} className="flex items-center gap-4 rounded-2xl border border-border p-5 hover:border-primary transition" data-testid="contact-email">
            <span className="h-11 w-11 grid place-items-center rounded-full bg-primary/10 text-primary"><Mail className="h-4 w-4" /></span>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</div>
              <div className="font-medium break-all">{brand.email}</div>
            </div>
          </a>
          <a href={brand.instagramUrl} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-2xl border border-border p-5 hover:border-primary transition" data-testid="contact-instagram">
            <span className="h-11 w-11 grid place-items-center rounded-full bg-primary/10 text-primary"><Instagram className="h-4 w-4" /></span>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Instagram</div>
              <div className="font-medium">@{brand.instagramHandle}</div>
            </div>
          </a>
          <div className="flex items-center gap-4 rounded-2xl border border-border p-5">
            <span className="h-11 w-11 grid place-items-center rounded-full bg-primary/10 text-primary"><MapPin className="h-4 w-4" /></span>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Office</div>
              <div className="font-medium">{brand.address}</div>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-border aspect-[16/10]">
            <iframe
              title="Map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=91.85%2C25.55%2C91.92%2C25.60&layer=mapnik&marker=25.5788%2C91.8933"
              className="h-full w-full"
              loading="lazy"
            />
          </div>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-3 rounded-3xl border border-border p-6 md:p-8 bg-card grid sm:grid-cols-2 gap-4" data-testid="contact-form">
          <div className="grid gap-2">
            <Label htmlFor="cn">Name</Label>
            <Input id="cn" required value={form.name} onChange={set("name")} data-testid="contact-name" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="ce">Email</Label>
            <Input id="ce" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp">Phone</Label>
            <Input id="cp" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cs">Subject</Label>
            <Input id="cs" value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="cm">Message</Label>
            <Textarea id="cm" required rows={6} value={form.message} onChange={set("message")} data-testid="contact-message" />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={loading} className="rounded-full h-12 w-full" data-testid="contact-submit">
              {loading ? "Sending…" : "Send message"}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
